import { type ComputedRef, type Ref, ref, watch } from 'vue'

import { handleLinksFilter, normalizeLinks } from '@/lib/adapters/links.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { Link, LinkListFilter, LinkRaw, LinkStatsParams } from '@/lib/types/links.types'
import { useErrorStore } from '@/stores/error.stores'

export const useGetLinks = (
  storeIdRef: ComputedRef<number | undefined>,
  filter: Ref<LinkListFilter> | ComputedRef<LinkListFilter>,
  userIdsRef: ComputedRef<number[]>,
) => {
  const data = ref<Link[]>([])
  const totalPages = ref(1)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchLinks = async (storeId: number, userIds: number[]) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<{ data: LinkRaw[]; last_page?: number }>(
        `/stores/${storeId}/linkStats`,
        { params: handleLinksFilter(filter.value, userIds) },
      )
      data.value = normalizeLinks(response.data.data)
      totalPages.value = response.data.last_page ?? 1
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => [storeIdRef.value, userIdsRef.value, { ...filter.value }] as const,
    async () => {
      const storeId = storeIdRef.value
      if (storeId) {
        await fetchLinks(storeId, userIdsRef.value)
      }
    },
    { immediate: true },
  )

  return { data, totalPages, isLoading, error }
}

export const buildLinksExportUrl = (
  storeId: number,
  filter: LinkListFilter,
  userIds: number[],
): string => {
  const params = new URLSearchParams()
  const built: LinkStatsParams = { ...handleLinksFilter(filter, userIds), format: 'xlsx' }
  for (const [key, value] of Object.entries(built)) {
    if (value !== undefined) {
      params.append(key, String(value))
    }
  }
  return `/stores/${storeId}/linkStats?${params.toString()}`
}
